import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@contexts/AuthContext';
import Modal from '@components/common/Modal';
import Toast from '@components/common/Toast';
import { createCase } from '@services/api/caseService';
import {
  CONTACT_TOPICS, TOPIC_FIELDS, CONTACT_DETAILS_FIELDS, getPrePopulatedMessage,
} from './TopicConfig';

/**
 * ContactFormModal — New Sandvik-approved single-page modal
 *
 * Layout: "Almost there! Do a quick check"
 * Two-column: Your needs (left) | Your details (right)
 *
 * Used by ALL 11 contact form topics.
 * Can be opened from:
 *   - Contact page (topic selector)
 *   - PLP/PDP "By request only" button
 *   - Cart "Request for Quote / Availability"
 *   - Outphased product "Contact us for further details"
 */

function FormField({ fieldKey, config, value, onChange, disabled }) {
  const id = `contact-${fieldKey}`;
  if (config.type === 'textarea') {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-n6 mb-1">
          {config.label} {config.required && <span className="text-error">*</span>}
        </label>
        <textarea id={id} value={value} onChange={e => onChange(fieldKey, e.target.value)}
          placeholder={config.placeholder} disabled={disabled} rows={4}
          className="w-full border border-n3 rounded px-3 py-2 text-sm resize-y
                     focus:ring-2 focus:ring-electric-blue focus:border-transparent
                     disabled:bg-n1 disabled:text-n5" />
      </div>
    );
  }
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-n6 mb-1">
        {config.label} {config.required && <span className="text-error">*</span>}
      </label>
      <input id={id} type={config.type} value={value} onChange={e => onChange(fieldKey, e.target.value)}
        placeholder={config.placeholder} disabled={disabled} min={config.min}
        className="w-full border border-n3 rounded px-3 py-2 text-sm
                   focus:ring-2 focus:ring-electric-blue focus:border-transparent
                   disabled:bg-n1 disabled:text-n5" />
    </div>
  );
}

export default function ContactFormModal({ isOpen, onClose, topicId, context = {} }) {
  const { account } = useAuth();
  const topic = CONTACT_TOPICS.find(t => t.id === topicId);

  const [formData, setFormData] = useState({});
  const [contactData, setContactData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!topic) return;
    const initial = {};
    topic.fields.forEach(f => { initial[f] = ''; });
    if (topic.prePopulate && initial.message !== undefined) {
      initial.message = getPrePopulatedMessage(topicId, context);
    }
    if (context.productName && initial.product !== undefined) {
      initial.product = context.sku ? `${context.productName} (${context.sku})` : context.productName;
    }
    if (context.quantity && initial.quantity !== undefined) {
      initial.quantity = String(context.quantity);
    }
    setFormData(initial);
    setSubmitted(false);

    const contact = {};
    CONTACT_DETAILS_FIELDS.forEach(f => { contact[f.key] = ''; });
    if (account) {
      const [firstName, ...rest] = (account.name || '').split(' ');
      contact.firstName = firstName || '';
      contact.lastName = rest.join(' ') || '';
      contact.email = account.email || '';
      contact.phone = account.phone || '';
      contact.company = account.company || '';
      contact.country = account.country || '';
    }
    setContactData(contact);
  }, [topicId, context, topic, account]);

  const handleFieldChange = useCallback((key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleContactChange = useCallback((key, value) => {
    setContactData(prev => ({ ...prev, [key]: value }));
  }, []);

  const isValid = useCallback(() => {
    if (!topic) return false;
    for (const fieldKey of topic.fields) {
      const config = TOPIC_FIELDS[fieldKey];
      if (config?.required && !formData[fieldKey]?.trim()) return false;
    }
    for (const field of CONTACT_DETAILS_FIELDS) {
      if (field.required && !contactData[field.key]?.trim()) return false;
    }
    return true;
  }, [topic, formData, contactData]);

  const handleSubmit = useCallback(async () => {
    if (!isValid() || !topic) return;
    setSubmitting(true);
    try {
      await createCase(topicId, formData, contactData);
      setSubmitted(true);
      setToast({ type: 'success', message: 'Your request has been submitted. We\'ll be in touch soon.' });
    } catch {
      setToast({ type: 'error', message: 'Failed to submit. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }, [isValid, topic, topicId, formData, contactData]);

  if (!topic) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} superTitle="Almost there! Do a quick check"
        title={topic.label} size="lg"
        footer={submitted ? (
          <div className="flex justify-center">
            <button onClick={onClose} className="px-6 py-2.5 bg-electric-blue text-white rounded text-sm font-medium hover:bg-blue-dark transition-colors">Done</button>
          </div>
        ) : (
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-6 py-2.5 border border-n3 rounded text-sm font-medium text-carbon-black hover:bg-n1 transition-colors">Cancel</button>
            <button onClick={handleSubmit} disabled={!isValid() || submitting}
              className="px-6 py-2.5 bg-electric-blue text-white rounded text-sm font-medium hover:bg-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        )}>
        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7FCC5B" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p className="text-lg font-medium text-carbon-black mb-2">Request submitted</p>
            <p className="text-sm text-n5">
              Your enquiry has been sent to our team. You can track its progress in the
              <span className="text-electric-blue"> Cases</span> section.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xs font-medium text-n5 tracking-wider mb-4 border-b border-n2 pb-2">Your needs</h3>
              <div className="space-y-4">
                {topic.fields.map(fieldKey => {
                  const config = TOPIC_FIELDS[fieldKey];
                  if (!config) return null;
                  return <FormField key={fieldKey} fieldKey={fieldKey} config={config} value={formData[fieldKey] || ''} onChange={handleFieldChange} />;
                })}
              </div>
            </div>
            <div>
              <h3 className="text-xs font-medium text-n5 tracking-wider mb-4 border-b border-n2 pb-2">Your details</h3>
              <div className="space-y-4">
                {CONTACT_DETAILS_FIELDS.map(field => (
                  <div key={field.key}>
                    <label htmlFor={`contact-detail-${field.key}`} className="block text-sm font-medium text-n6 mb-1">
                      {field.label} {field.required && <span className="text-error">*</span>}
                    </label>
                    <input id={`contact-detail-${field.key}`} type={field.type} value={contactData[field.key] || ''}
                      onChange={e => handleContactChange(field.key, e.target.value)}
                      className="w-full border border-n3 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-electric-blue focus:border-transparent" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </>
  );
}
